import { scheduleBoot } from "./3os-living-r1c-p1-boot-gate.js";
/* ===========================================================================
   conversion-207-r1b.js · 207-FINAL-R1B — Conversion Spine
   ---------------------------------------------------------------------------
   The last script on all 22 routes. It adds a commercial spine to the R1A
   experience and it adds nothing else: no redesign, no second visual family, no
   dependency, no network call, no cookie, no storage key of its own.

   Four jobs, in this order:

     1. LOCAL MEASUREMENT (§5.9). An in-memory buffer plus one CustomEvent per
        commercial gesture. Five event names, five allowed fields, zero personal
        data, zero requests. It exists so this slice can be QA'd locally and so
        the Shopify migration has a mapping to write against.

     2. FIXED-FORMAT QUICK-ADD (§5.4 / §6). `data-pd-fixed-add="500|1000"` is a
        deliberately DIFFERENT contract from `data-pd-add`. `paintSelection()`
        rewrites `data-format` on every `[data-pd-add]` button to follow the
        global selector; a Match Point button for 1 L must stay a 1 L button
        even while the page selector shows 500 ml. The store is the same one —
        `commerce.add()` from product-desire.js — so there is exactly one cart
        and exactly one subtotal in the page.

     3. CART COMPANION (§5.8). Four states, painted from the same render pass as
        the cart lines: empty · only 500 ml · only 1 L · both. When one format is
        missing it offers that format with its own fixed quick-add. No discount,
        no free shipping, no countdown, no stock, no pressure.

     4. BUY-PATH INTENT. The header CTA and every product link stay real links;
        this layer only records that they were used. Without JavaScript nothing
        here runs, the links still work, and no quick-add is visible (the CSS
        layer hides them under `html.no-js`), so no button can lie about having
        added something.

   All copy and all values come from commerce-data.js.
   =========================================================================== */

import { PRODUCT, FORMATS } from "./3os-living-r1c-commerce-data-245.js";
import { commerce } from "./3os-living-r1c-product-desire.js?v=276-20260903-r2";

const $ = (selector, scope = document) => scope?.querySelector(selector) ?? null;
const $$ = (selector, scope = document) => [...(scope?.querySelectorAll(selector) ?? [])];

/* --------------------------------------------------------------------------
   1 · Local measurement contract
   ---------------------------------------------------------------------------
   `window.__THREE_OLIVES_EVENTS__` is an array in this document's memory. It is
   never serialised, never persisted and never sent anywhere. The payload is
   whitelisted field by field, so a future caller cannot smuggle an identifier
   into it, and the buffer has a ceiling so a long session cannot grow it
   without bound.
   -------------------------------------------------------------------------- */
const EVENT_BUFFER_MAX = 200;
const ALLOWED = new Set(PRODUCT.events.names);

const buffer = (() => {
  const key = PRODUCT.events.bufferKey;
  if (!Array.isArray(window[key])) window[key] = [];
  return window[key];
})();

function emit(name, detail = {}) {
  if (!ALLOWED.has(name)) return null;

  /* Exactly the five allowed fields, in a fixed order, and only when they carry
     a real value. `event` is always present. */
  const payload = { event: name };
  if (detail.source) payload.source = String(detail.source);
  if (detail.format && FORMATS[detail.format]) payload.format = String(detail.format);
  if (Number.isFinite(detail.quantity)) payload.quantity = detail.quantity;
  if (Number.isFinite(detail.value)) payload.value = detail.value;

  buffer.push(payload);
  if (buffer.length > EVENT_BUFFER_MAX) buffer.splice(0, buffer.length - EVENT_BUFFER_MAX);

  window.dispatchEvent(new CustomEvent(PRODUCT.events.channel, { detail: payload }));
  return payload;
}

/* --------------------------------------------------------------------------
   2 · Cart companion
   -------------------------------------------------------------------------- */
const COMPANION_CLASS = "tos-companion";
const companions = [];

function textEl(tag, className, text) {
  const node = document.createElement(tag);
  node.className = className;
  node.textContent = text;
  return node;
}

/** The fixed quick-add for one format. Never `data-pd-add`. */
function fixedAdd(format, source, { block = true } = {}) {
  const button = document.createElement("button");
  button.type = "button";
  button.className = block ? "pd-add tos-companion__add" : "pd-add";
  button.dataset.pdFixedAdd = format.key;
  button.dataset.tosSource = source;
  button.textContent = format.addLabel;
  return button;
}

function formatLink(format, source) {
  const link = document.createElement("a");
  link.className = "tos-companion__link";
  link.href = format.url;
  link.dataset.tosSource = source;
  link.append(
    textEl("b", "tos-companion__link-label", format.label),
    textEl("span", "tos-companion__link-price", `${format.price} · ${format.unitPrice}`),
  );
  return link;
}

/** `empty` mounts live inside the empty state; `filled` mounts inside the list. */
function buildCompanion(kind) {
  const box = document.createElement(kind === "empty" ? "div" : "aside");
  box.className = `${COMPANION_CLASS} ${COMPANION_CLASS}--${kind}`;
  box.dataset.tosCompanion = kind;
  box.dataset.state = "empty";
  return box;
}

function paintCompanion(box, state) {
  if (box.dataset.state === state && box.dataset.painted === "true") return;
  box.dataset.state = state;
  box.dataset.painted = "true";
  box.replaceChildren();

  const label = textEl("p", `${COMPANION_CLASS}__label`, PRODUCT.companion.label);

  if (state === "empty") {
    const links = document.createElement("div");
    links.className = `${COMPANION_CLASS}__links`;
    links.append(
      formatLink(FORMATS["500"], "cart_companion_empty"),
      formatLink(FORMATS["1000"], "cart_companion_empty"),
    );
    box.append(
      label,
      textEl("p", `${COMPANION_CLASS}__title`, PRODUCT.companion.empty.title),
      textEl("p", `${COMPANION_CLASS}__body`, PRODUCT.companion.empty.body),
      links,
    );
    return;
  }

  if (state === "both") {
    box.append(
      label,
      textEl("p", `${COMPANION_CLASS}__title`, PRODUCT.companion.both.title),
      textEl("p", `${COMPANION_CLASS}__body`, PRODUCT.companion.both.body),
    );
    return;
  }

  /* One format is in the cart, the other one is the suggestion. The reading
     comes first and the action last. Asset provenance stays internal; the
     customer sees the product, its use and its live commercial truth. */
  const missing = FORMATS[state === "only-500" ? "1000" : "500"];

  const unit = textEl("p", `${COMPANION_CLASS}__unit`, `${missing.price} · ${missing.unitPrice}`);

  const act = document.createElement("div");
  act.className = `${COMPANION_CLASS}__act`;
  act.append(fixedAdd(missing, "cart_companion"));

  box.append(
    label,
    textEl("p", `${COMPANION_CLASS}__title`, missing.suggestTitle),
    textEl("p", `${COMPANION_CLASS}__body`, missing.suggestBody),
    unit,
    act,
  );
}

function cartState() {
  const has500 = commerce.has("500");
  const has1000 = commerce.has("1000");
  if (!has500 && !has1000) return "empty";
  if (has500 && has1000) return "both";
  return has500 ? "only-500" : "only-1000";
}

function renderCompanions() {
  const state = cartState();
  companions.forEach((box) => {
    /* The empty mount only ever speaks about an empty cart; the filled mount
       only ever speaks about a cart with lines in it. Whichever container is
       hidden by the cart renderer takes its companion with it. */
    if (box.dataset.tosCompanion === "empty") paintCompanion(box, "empty");
    else if (state !== "empty") paintCompanion(box, state);
  });
}

function mountCompanions() {
  const scopes = [$("[data-cart-dialog]"), $("[data-cart-page]")].filter(Boolean);

  scopes.forEach((scope) => {
    const empty = $("[data-cart-empty]", scope);
    if (empty && !$(`[data-tos-companion="empty"]`, empty)) {
      const box = buildCompanion("empty");
      empty.append(box);
      companions.push(box);
    }

    const filled = $("[data-cart-filled]", scope);
    if (filled && !$(`[data-tos-companion="filled"]`, filled)) {
      const box = buildCompanion("filled");
      /* Above the subtotal and the checkout button: a suggestion belongs to the
         basket, not to the payment step. */
      const foot = $(".house-cart__foot, .cart-page__summary", filled);
      if (foot) foot.before(box);
      else filled.append(box);
      companions.push(box);
    }
  });
}

/* --------------------------------------------------------------------------
   3 · Wiring
   -------------------------------------------------------------------------- */
function wireFixedAdds() {
  /* Delegated, because the companion's own buttons are created after boot. */
  document.addEventListener("click", (event) => {
    const button = event.target.closest("[data-pd-fixed-add]");
    if (!button) return;
    const key = button.dataset.pdFixedAdd;
    if (!FORMATS[key]) return;
    event.preventDefault();
    commerce.add(key, 1, button, button.dataset.tosSource || "fixed_add");
  });
}

function wireBuyPath() {
  document.addEventListener("click", (event) => {
    const link = event.target.closest("[data-tos-buy], [data-tos-source]");
    if (!link || link.matches("[data-pd-fixed-add]")) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const source = link.dataset.tosSource || "header";
    const format = link.dataset.tosFormat || null;
    emit("buy_path_click", { source, format });
    /* No preventDefault: these are real links and they must keep navigating. */
  });
}

function boot() {
  mountCompanions();
  renderCompanions();
  wireFixedAdds();
  wireBuyPath();

  commerce.subscribe((payload) => {
    if (!payload || typeof payload !== "object") return;
    if (payload.type === "cart_change") {
      renderCompanions();
      return;
    }
    emit(payload.type, payload);
  });
}

/* R1C-M1-P1 — boot after first paint, own macrotask, same order */
scheduleBoot(boot);
